// eslint-disable-next-line jira/filename-case
import { useCallback } from 'react';
import type { TenantContext } from '@jira/platform__tenant-context-types/src/tenant-context.tsx';
import getPillarLocalDevelopment from '@jira/ssr__global-build-context/src/getPillarLocalDevelopment.tsx';
import { isStableUIEnabledForTests } from '@jira/platform__is-stable-ui-enabled-for-tests/src/isStableUIEnabledForTests.tsx';
import { performGetRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { buildBasePath } from '@atlassian/post-office-context/build-base-path';
import type { PostOfficeEnvironmentValues } from '@atlassian/post-office-context/post-office-environment/types';
import type { PostOfficeContextValue } from '@atlassian/post-office-context/types';
import { requestInitialData } from '@atlassian/post-office-placement-shared';
import { useResource, type RouteResource } from '@atlassian/react-resource-router';

import { fg } from '@atlassian/jira-feature-gating';
import {
	type JiraTenantDataFields,
	tenantToPostOfficeContext,
	transformJiraEnvToPostOfficeEnvironment,
} from './tenant-to-post-office-context.tsx';

type OptionalEnvConfigsForPostOfficeEnvironment = Omit<PostOfficeEnvironmentValues, 'envConfigs'> &
	Partial<Pick<PostOfficeEnvironmentValues, 'envConfigs'>>;

/**
 * Common method for setting up the Post Office placement request URL with the environment and product context
 *
 * @param placementId
 * @param contextOptions
 * @returns
 */
const preloadPostOfficePlacement = async (
	placementId: string,
	contextOptions: {
		postOfficeContext: PostOfficeContextValue;
		environment: OptionalEnvConfigsForPostOfficeEnvironment;
	},
) => {
	const { postOfficeContext, environment } = contextOptions;

	const jiraFetcherWithS2SHeaders = async (url: string) => {
		let hostEnv = 'prod';
		if (environment.currentEnv !== 'production') {
			hostEnv = 'staging';
		}

		const fetchHeaders = {
			'x-ssr-service-name': 'post-office', // Tells Tesseract to S2S with `post-office`
			'x-ssr-service-env': hostEnv, // Defaults to current Env, however Post Office is not in dev
		};

		return performGetRequest(url.replace('gateway/api/post-office/', ''), {
			headers: fetchHeaders,
		});
	};

	return requestInitialData(
		placementId,
		postOfficeContext,
		__SERVER__ && !getPillarLocalDevelopment() ? jiraFetcherWithS2SHeaders : performGetRequest,
		buildBasePath(environment.currentEnv, environment.envConfigs),
	);
};

/**
 * Returns a fetcher for Post Office placement API that is shared with the SSR preload
 *
 * @param placementId
 * @returns
 */
export const usePlacementSSRFetch = (
	resource: RouteResource,
): (() => Promise<Response> | undefined) => {
	const { data } = useResource(resource);

	return useCallback(() => {
		if (data) {
			/**
			 * Preloaded data must be serialized, however our initialData is expected to be in the form of a Response promise.
			 * TODO: Remove this workaround once the Post Office placement API is updated to handle flat data.
			 */
			const init = {
				status: 200,
				statusText: 'ok!',
			};

			return Promise.resolve(new Response(JSON.stringify(data), init));
		}
	}, [data]);
};

/**
 * Returns the Post Office placement initial data from the cache if it exists
 *
 * @param placementId
 * @returns Post Office placement initial data from cache
 */
export const usePlacementPreloadedData = (resource: RouteResource): [unknown, () => void] => {
	const { data, clear } = useResource(resource);

	return [data, clear];
};

/**
 * Preload a Post Office placement API via SSR
 *
 * @param placementId
 * @param preloadedTenantContext
 * @returns
 */
export const preloadPostOfficePlacementWithTenantContext = async (
	placementId: string,
	preloadedTenantContext: Pick<TenantContext, JiraTenantDataFields | 'environment'>,
): Promise<Response | null | undefined> => {
	if (fg('jpx-870-stable-ui-for-criterion')) {
		if (isStableUIEnabledForTests()) {
			return Promise.resolve(null);
		}
	}

	return preloadPostOfficePlacement(placementId, {
		postOfficeContext: tenantToPostOfficeContext(preloadedTenantContext),
		environment: {
			currentEnv: transformJiraEnvToPostOfficeEnvironment(preloadedTenantContext.environment),
		},
	});
};
